/* eslint-disable func-names */
import { Layout, UploadItem } from '@vwfs-bronson/bronson-react';
import { AxiosResponse } from 'axios';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { affordabilityDocumentUploadPageSlice } from '../../screens/NewQuotation/AffordabilityDocumentUploadPage/AffordabilityDocumentUploadPage.slice';
import { fileSizeFormat } from '../../services/common/fileSizeFormat';
import {
  AffordabilityDocumentUploadResponseModel,
  deleteAffordabilityDocument,
  uploadAffordabilityDocument,
} from '../../services/redux/features/api/affordabilityDocument.api';
import { UploadedFile } from '../../types/files';

const MAX_FILE_SIZE_BYTES = 4.5 * 1024 * 1024;

interface DocumentUploadItemProps {
  file: UploadedFile;
  onDeleteSuccess: (...args: any[]) => void;
  validityCallback: (isValid: boolean) => void;
  onUploadSuccess: (uploadedFileId: string) => void;
}

export const AffordabilityDocumentUploadItem: FunctionComponent<DocumentUploadItemProps> = ({
  file,
  onDeleteSuccess,
  validityCallback: _validityCallback,
  onUploadSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [bytesSent, setBytesSent] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadFailed, setIsUploadFailed] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState<string>();

  const setFailedUploadFlags = () => {
    setIsUploading(false);
    setIsUploadFailed(true);
  };
  const setSuccessUploadFlags = () => {
    setIsUploading(false);
    setIsUploadFailed(false);
  };

  const onUploadProgress = (percentCompleted: number, bytesSent: number, totalBytes: number) => {
    setUploadProgress(percentCompleted);
    setTotalBytes(totalBytes);
    setBytesSent(bytesSent);
  };

  const autoUploadFile = async () => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFailedUploadFlags();
      return;
    }

    setIsUploading(true);

    let response: AxiosResponse<AffordabilityDocumentUploadResponseModel>;

    try {
      response = await uploadAffordabilityDocument(file, onUploadProgress);

      dispatch(
        affordabilityDocumentUploadPageSlice.actions.pushFile({
          uploadedFile: {
            filename: file.name,
            id: response.data.id,
            isFileValid: false,
            isUploaded: true,
            size: file.size,
            mimeType: file.type,
          },
        })
      );

      setUploadedFileId(response.data.id);
      setSuccessUploadFlags();
      onUploadSuccess(response.data.id);
    } catch {
      setFailedUploadFlags();
    }
  };

  const deleteFile = async () => {
    if (!uploadedFileId) {
      return;
    }
    try {
      await deleteAffordabilityDocument(uploadedFileId);
      dispatch(affordabilityDocumentUploadPageSlice.actions.removeFileById({ id: uploadedFileId }));
      onDeleteSuccess();
    } catch (e) {
      throw new Error();
    }
  };

  const previewFile = () => {
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      window.open(blobUrl, '_blank');
    }
  };

  useEffect(() => {
    autoUploadFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <Layout.Item default="1/1">
        <UploadItem
          fileName={file.name}
          fileSize={fileSizeFormat(totalBytes)}
          icon="semantic-file"
          loading={isUploading}
          error={isUploadFailed}
          message={isUploadFailed && t('documentUploadItem:fileTooBig')}
          progressValue={uploadProgress.toString()}
          filesizeProgress={fileSizeFormat(bytesSent)}
          mainButtonProps={{
            'aria-label': t('documentUploadItem.viewButton'),
            title: t('documentUploadItem.viewButton'),
          }}
          onMainButtonClick={previewFile}
          onSecondaryButtonClick={deleteFile}
          secondaryButtonProps={{
            'aria-label': t('documentUploadItem.deleteButton'),
            title: t('documentUploadItem.deleteButton'),
          }}
        />
      </Layout.Item>
    </Layout>
  );
};
